import {
  Badge,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import {
  BUSINESS_MODELS,
  LEAD_SOURCES,
  useUpdateLead,
  type Lead,
  type LeadSource,
} from '@/models/leads';
import {
  referringAgentOptions,
  useReferringAgents,
} from '@/models/referringAgents';
import { FormSaveBar } from '@/components/FormSaveBar';
import { CallsSection } from '@/components/CallsCard';
import { useCardForm } from '@/lib/useCardForm';

type Values = {
  source: LeadSource;
  sourceDetail: string;
  website: string;
  businessModel: string | null;
  originUrl: string;
  path: string;
  referringAgentId: number | null;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  marketingCampaign: string;
  marketingChannel: string;
  affiliate: string;
  affiliateSource: string;
};

export function MarketingCard({
  lead,
  readOnly,
}: {
  lead: Lead;
  readOnly: boolean;
}) {
  const update = useUpdateLead();
  const agents = useReferringAgents('', undefined, 200);
  const { form, dirty, saving, handleSubmit } = useCardForm<Values>({
    initialValues: {
      source: lead.source,
      sourceDetail: lead.sourceDetail ?? '',
      website: lead.website ?? '',
      businessModel: lead.businessModel ?? null,
      originUrl: lead.originUrl ?? '',
      path: lead.path ?? '',
      referringAgentId: lead.referringAgentId ?? null,
      utmSource: lead.utmSource ?? '',
      utmMedium: lead.utmMedium ?? '',
      utmCampaign: lead.utmCampaign ?? '',
      marketingCampaign: lead.marketingCampaign ?? '',
      marketingChannel: lead.marketingChannel ?? '',
      affiliate: lead.affiliate ?? '',
      affiliateSource: lead.affiliateSource ?? '',
    },
    enabled: !readOnly,
    save: (dirtyValues) => update.mutateAsync({ id: lead.id, ...dirtyValues }),
    successMessage: 'Marketing saved',
  });

  // IS-106: `source` is the normalised analytics category and staff may correct it.
  // Show the untouched original selection whenever it says something more — extra
  // labels, or wording the BO enum never had.
  const originalLabels = (lead.sourceLabels ?? []).filter(
    (label) => label !== form.values.source,
  );

  return (
    <CollapsibleCard title="Marketing">
      <form onSubmit={handleSubmit}>
        <Stack gap="xs">
          <Group grow gap="xs">
            <Select
              label="Source"
              size="xs"
              data={LEAD_SOURCES}
              searchable
              disabled={readOnly}
              value={form.values.source}
              onChange={(v) => {
                if (v) form.setFieldValue('source', v as LeadSource);
              }}
            />
            <TextInput
              label="Source detail"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('sourceDetail')}
            />
          </Group>
          {originalLabels.length > 0 && (
            <Group gap={4} align="center">
              <Text size="xs" c="dimmed">
                Originally selected:
              </Text>
              {originalLabels.map((label) => (
                <Badge key={label} size="xs" variant="light" color="gray">
                  {label}
                </Badge>
              ))}
            </Group>
          )}
          <Group grow gap="xs">
            <TextInput
              label="Website"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('website')}
            />
            <Select
              label="Business model"
              size="xs"
              data={BUSINESS_MODELS}
              clearable
              disabled={readOnly}
              value={form.values.businessModel}
              onChange={(v) => form.setFieldValue('businessModel', v)}
            />
          </Group>
          <Group grow gap="xs">
            <TextInput
              label="Origin URL"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('originUrl')}
            />
            <Select
              label="Referred by"
              size="xs"
              placeholder="No referring agent"
              data={referringAgentOptions(
                agents.data?.agents,
                lead.referringAgentId,
                lead.referringAgentName,
              )}
              value={
                form.values.referringAgentId == null
                  ? null
                  : String(form.values.referringAgentId)
              }
              onChange={(v) =>
                form.setFieldValue('referringAgentId', v ? Number(v) : null)
              }
              searchable
              clearable
              disabled={readOnly}
            />
          </Group>
          <Group grow gap="xs">
            <TextInput
              label="UTM source"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('utmSource')}
            />
            <TextInput
              label="UTM medium"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('utmMedium')}
            />
            <TextInput
              label="UTM campaign"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('utmCampaign')}
            />
          </Group>
          <Group grow gap="xs">
            <TextInput
              label="Marketing campaign"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('marketingCampaign')}
            />
            <TextInput
              label="Marketing channel"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('marketingChannel')}
            />
          </Group>
          <Group grow gap="xs">
            <TextInput
              label="Affiliate"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('affiliate')}
            />
            <TextInput
              label="Affiliate source"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('affiliateSource')}
            />
          </Group>
          <TextInput
            label="Path"
            size="xs"
            disabled={readOnly}
            {...form.getInputProps('path')}
          />
          <Group gap="lg">
            {lead.ip && (
              <Text size="xs" c="dimmed">
                IP: {lead.ip}
              </Text>
            )}
            {lead.mondayItemId && (
              <Text size="xs" c="dimmed">
                Monday item: {lead.mondayItemId}
              </Text>
            )}
            <Badge
              size="sm"
              variant="light"
              color={
                lead.gdprConsent == null
                  ? 'gray'
                  : lead.gdprConsent
                    ? 'green'
                    : 'red'
              }
            >
              GDPR:{' '}
              {lead.gdprConsent == null
                ? 'Unknown'
                : lead.gdprConsent
                  ? 'Consent given'
                  : 'Declined'}
            </Badge>
          </Group>
          {dirty && !readOnly && (
            <FormSaveBar saving={saving} onDiscard={form.reset} />
          )}
        </Stack>
      </form>
      <Divider my="sm" />
      <CallsSection leadId={lead.id} />
    </CollapsibleCard>
  );
}
